import React, { useEffect, useState } from 'react';
import {
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
} from 'react-accessible-accordion';
import { Grid, GridRow, GridItem } from './../Grid';
import { Text, Title } from './../Typography';
import {
  Loading,
  LocationComponentFilterItem,
  LocationDots,
  StyledLocationComponentAccordion,
  StyledLocationComponentAccordionItemPanel,
} from './styles';
import {
  ILocationComponentAccordionItem,
  ILocationComponentProps,
  LocationComponentFilterType,
} from './types';
import { getCopy } from '../../lib/utils/getCopy';
import { useRouter } from 'next/router';
import { getLocale } from '../../modules/localesMapper';

const LocationItem = ({ getItemBody, name, index, item, copy, locale, market }: any) => {
  const [loadBody, setLoadBody] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bodyComponents, setBodyComponents] = useState([]);

  useEffect(() => {
    async function fetchBody() {
      // load body components
      setIsLoading(true);
      try {
        const response = await fetch(`/api/location-item`, {
          method: 'POST',
          body: JSON.stringify({ id: item.id, locale, market }),
        });
        const { data } = await response.json();
        setBodyComponents(data?.locationItem?.bodyComponents);
        setIsLoading(false);
      } catch (e) {
        console.error('Error fetching location item', e);
        setIsLoading(false);
      }
    }
    if (loadBody && bodyComponents.length === 0) {
      fetchBody();
    }
  }, [loadBody]);

  return (
    <AccordionItem uuid={`${name}-${index}`} data-test={[`${item?.id}-item`]}>
      <AccordionItemHeading
        data-test={[`${item?.id}-item-heading`]}
        onClick={() => setLoadBody(true)}
        data-index={index}
      >
        <AccordionItemButton
          data-test={[`${item?.id}-item-button`]}
          data-print="hide-before-content"
        >
          <div id="title" data-test={[`${item?.id}-title`]} className="w-4/5">
            <Title tag="div" type="xs">
              {item.locationName}
            </Title>
            <Text tag="p" type="md">
              {item?.date}
              <LocationDots data-test="dots-info-show" className="inline-block" />
              {item.numberOfEmployees} {getCopy('employees', copy)}
              {item.isProductionSite ? (
                <>
                  <LocationDots data-test="dots-info-show" className="inline-block" />
                  {getCopy('production_site', copy)}
                </>
              ) : (
                ''
              )}
            </Text>
          </div>
        </AccordionItemButton>
      </AccordionItemHeading>
      <StyledLocationComponentAccordionItemPanel
        data-test={[`${item?.id}-panel`]}
        $isNumbered={false}
        $hasTitle
        $forceTitleSpace="none"
        data-print="text-visible"
      >
        <Text tag="p" type="md">
          {item.description}
        </Text>
        {isLoading && (
          <Loading tag="p" type="md" className="mt-4">
            Loading...
          </Loading>
        )}
        {bodyComponents && getItemBody(bodyComponents)}
      </StyledLocationComponentAccordionItemPanel>
    </AccordionItem>
  );
};

export function LocationComponent({
  id = 'LocationComponent',
  data,
  getItemBody,
  copy,
}: ILocationComponentProps): JSX.Element {
  const name = 'LocationComponent';
  const [filter, setFilter] = useState<LocationComponentFilterType>('all');
  const { locale: market } = useRouter();
  const locale = getLocale(market);
  const { locationItems } = data;

  // @TODO replace with a function to fetch only the data we need
  const filteredItems = locationItems.filter((item: ILocationComponentAccordionItem) => {
    if (filter === 'all') {
      return true;
    }
    if (filter === 'production' && item.isProductionSite) {
      return true;
    }
    return false;
  });

  const mapImg = filter === 'all' ? data.mapAllSites : data.mapProductionSites;
  return (
    <Grid>
      <GridRow columns={16}>
        <GridItem colSpan={16}>
          <LocationComponentFilterItem
            type="button"
            onClick={() => setFilter('all')}
            $active={filter === 'all'}
          >
            <Text tag="span" type="md" className="whitespace-nowrap">
              {getCopy('all_locations', copy)}
            </Text>
          </LocationComponentFilterItem>
          <LocationComponentFilterItem
            type="button"
            onClick={() => setFilter('production')}
            $active={filter === 'production'}
          >
            <Text tag="span" type="md" className="whitespace-nowrap">
              {getCopy('production_site', copy)}
            </Text>
          </LocationComponentFilterItem>
        </GridItem>
        <GridItem colSpan={16}>
          <img className="mb-10" src={mapImg?.asset[0].url} alt={mapImg?.asset[0].alt} />
        </GridItem>
        <GridItem colStart={2} colSpan={14}>
          <StyledLocationComponentAccordion
            allowMultipleExpanded
            allowZeroExpanded
            $titleTogglePos="center"
          >
            {filteredItems.map((item: ILocationComponentAccordionItem, index: number) => (
              <LocationItem
                key={`${name}-${index}`}
                getItemBody={getItemBody}
                market={market}
                locale={locale}
                name={name}
                index={index}
                item={item}
                copy={copy}
              />
            ))}
          </StyledLocationComponentAccordion>
        </GridItem>
      </GridRow>
    </Grid>
  );
}

LocationComponent.defaultProps = {
  id: 'LocationComponent',
};
